export interface ServiceAreaFaq {
    question: string;
    answer: string;
    isOpen?: boolean;
}

export interface OtherServiceAreaLink {
    name: string;
    route: string;
}

export interface QuoteProduct {
    tag: string;
    title: string;
    price: string;
    description: string;
    linkUrl: string;
    queryParams: { product: string };
    highlight?: boolean;
}

export interface TestimonialItem {
    quote: string;
    author: string;
    affiliation: string;
    avatarUrl: string;
}

export interface StepItem {
    number: string;
    title: string;
    description: string;
    dark?: boolean;
}

export interface ServiceAreaData {
    id: string;
    name: string;
    pageTitle: string;
    heroTitlePrefix: string;
    heroHighlight: string;
    heroDescription: string;
    phone: string;
    phoneTel: string;
    communities: string[];
    quotesSectionLabel: string;
    faqs: ServiceAreaFaq[];
    otherAreas: OtherServiceAreaLink[];
}

export const SHARED_STEPS: StepItem[] = [
    {
        number: '01',
        title: 'Submit the inspection report',
        description: "Send us the report with the flagged repair and replacement items. That's the whole ask."
    },
    {
        number: '02',
        title: 'Get a neutral quote in 24–72 hours',
        description: 'One consistent number, 7 days a week, favoring neither buyer nor seller.'
    },
    {
        number: '03',
        title: 'speKwo completes the repairs',
        description: 'Approve the work and our full-time in-house tradesmen do the repairs at the price quoted.',
        dark: true
    }
];

export const SHARED_PRODUCTS: QuoteProduct[] = [
    {
        tag: 'FULL REPORT',
        title: 'The Full speKwo',
        price: '$89',
        description: 'Every repair/replace item on the report, quoted.',
        linkUrl: '/spekwo-products',
        queryParams: { product: 'full' },
        highlight: true
    },
    {
        tag: 'CLIENT CHOICE',
        title: 'Itemized Solution Estimate',
        price: '$59',
        description: 'Up to 40 items you choose from your inspection.',
        linkUrl: '/spekwo-products',
        queryParams: { product: 'itemized' }
    },
    {
        tag: 'FOR SELLERS',
        title: 'Pre-List Quote',
        price: '$59',
        description: '1–2 pictures per item, before you list.',
        linkUrl: '/spekwo-products',
        queryParams: { product: 'prelist' }
    }
];

export const SHARED_TESTIMONIALS: TestimonialItem[] = [
    {
        quote: 'Spekwo has helped me buy back precious time in my schedule, at an incredibly low cost. Every quote received has saved me so many hours of time calling and driving to meet contractors. If you are a full-time agent, this service is a must.',
        author: 'SCOTT H.',
        affiliation: 'TRADE & TRYON REALTY',
        avatarUrl: 'https://api.spekwo.com/bannerImages/2/2_Scott%20H.png'
    },
    {
        quote: 'I always follow up with my clients after they have work completed with your company and I am yet to have any disappointed clients! Your process is easy and convenient to use, which compliments a smooth process for my clients.',
        author: 'JUDI S.',
        affiliation: 'KELLER WILLIAMS BALLANTYNE',
        avatarUrl: 'https://api.spekwo.com/bannerImages/1/1_Judi%20S.png'
    },
    {
        quote: 'Because I constantly deal with repairs and renovations, I have a pretty good idea of what fair and reasonable contractors charge, and spekwo was exactly that. I have been using them for my client’s deals and highly recommend them.',
        author: 'CLAUDIA O.',
        affiliation: 'COMPASS',
        avatarUrl: 'https://api.spekwo.com/bannerImages/6/6_Claudia%20O.png'
    }
];

export const SERVICE_AREAS_DATA: Record<string, ServiceAreaData> = {
    charlotte: {
        id: 'charlotte',
        name: 'Charlotte, NC',
        pageTitle: 'Home Inspection Repair Quotes in Charlotte, NC | 24–72 Hours | speKwo',
        heroTitlePrefix: 'Home inspection repair quotes<br class="d-br">in ',
        heroHighlight: 'Charlotte, NC',
        heroDescription: 'Charlotte is where speKwo was founded, and where our full-time tradesmen have worked through more inspection reports than anywhere else. When a due diligence deadline is bearing down in the Queen City’s fast-moving market, we return one neutral, consistent repair quote in 24–72 hours, 7 days a week, so buyers, sellers, and agents can agree on a number and keep the deal moving.',
        phone: '704.626.1776',
        phoneTel: 'tel:7046261776',
        communities: [
            'Uptown & South End',
            'Ballantyne',
            'Lake Norman · Cornelius · Davidson',
            'Huntersville',
            'Matthews & Mint Hill',
            'Concord & Kannapolis',
            'Gastonia',
            'Fort Mill & Rock Hill, SC',
            '+ Surrounding areas'
        ],
        quotesSectionLabel: '02 / QUOTES FOR CHARLOTTE, NC',
        faqs: [
            {
                question: 'How fast can I get a repair quote in Charlotte?',
                answer: 'Within 24–72 hours of submitting your inspection report, 7 days a week. Charlotte is our home market, and our office is just up the road in Cornelius.',
                isOpen: false
            },
            {
                question: 'Do you complete the repairs in the Charlotte area too?',
                answer: 'Yes. All repairs, pre or post close, are completed by full-time tradesmen who only work for speKwo, so we control quality and scheduling across the Charlotte metro. A $1,500 repair minimum applies, and quotes are honored at the price quoted.',
                isOpen: false
            },
            {
                question: 'How much does a repair quote cost?',
                answer: 'Quotes start at $59. The Itemized Solution Estimate and Pre-List Quote are $59, and The Full speKwo (every item on the report) is $89. Complete over $1,500 of repairs with speKwo and your quote cost is refunded.',
                isOpen: false
            }
        ],
        otherAreas: [
            { name: 'Raleigh', route: '/service-area-raleigh' },
            { name: 'Asheville', route: '/service-area-asheville' },
            { name: 'the Triad', route: '/service-area-triad' },
            { name: 'Upstate SC', route: '/service-area-upstate-sc' }
        ]
    },
    raleigh: {
        id: 'raleigh',
        name: 'Raleigh, NC',
        pageTitle: 'Home Inspection Repair Quotes in Raleigh, NC | 24–72 Hours | speKwo',
        heroTitlePrefix: 'Home inspection repair quotes<br class="d-br">in ',
        heroHighlight: 'Raleigh, NC',
        heroDescription: 'Triangle deals move fast, and a single disputed repair item can stall a contract in Raleigh, Durham, or Cary while the due diligence clock runs down. speKwo returns one neutral repair quote on your inspection report in 24–72 hours, 7 days a week, so both sides can trust the number and negotiate from facts instead of guesses.',
        phone: '704.626.1776',
        phoneTel: 'tel:7046261776',
        communities: [
            'Raleigh',
            'Durham',
            'Cary',
            'Apex',
            'Wake Forest',
            'Holly Springs',
            'Garner',
            'Knightdale',
            '+ Surrounding areas'
        ],
        quotesSectionLabel: '02 / QUOTES FOR RALEIGH, NC',
        faqs: [
            {
                question: 'Do you serve the whole Triangle, or just Raleigh?',
                answer: 'The whole Triangle — Raleigh, Durham, Cary, Apex, Wake Forest, Holly Springs, Garner, and surrounding communities all fall inside our Raleigh service area.',
                isOpen: false
            },
            {
                question: 'How does a quote work if you’re not local to my street?',
                answer: 'Our pricing draws on professional contractor price lists, average labor costs, and the time each task requires, adjusted for the Triangle. Every quote is manually reviewed by our department experts before it’s sent, and if we do the work, it’s at the price quoted.',
                isOpen: false
            },
            {
                question: 'How much does a repair quote cost?',
                answer: 'Quotes start at $59. The Itemized Solution Estimate and Pre-List Quote are $59, and The Full speKwo (every item on the report) is $89. Complete over $1,500 of repairs with speKwo and your quote cost is refunded.',
                isOpen: false
            }
        ],
        otherAreas: [
            { name: 'Charlotte', route: '/service-area-charlotte' },
            { name: 'Asheville', route: '/service-area-asheville' },
            { name: 'the Triad', route: '/service-area-triad' },
            { name: 'Upstate SC', route: '/service-area-upstate-sc' }
        ]
    },
    asheville: {
        id: 'asheville',
        name: 'Asheville, NC',
        pageTitle: 'Home Inspection Repair Quotes in Asheville, NC | 24–72 Hours | speKwo',
        heroTitlePrefix: 'Home inspection repair quotes<br class="d-br">in ',
        heroHighlight: 'Asheville, NC',
        heroDescription: 'Mountain homes come with mountain inspection reports — crawlspaces, moisture, aging plumbing and electrical, and rooflines that have seen real weather. speKwo puts one neutral, consistent price on every flagged item in 24–72 hours, 7 days a week, so Asheville buyers and sellers can stop guessing what “assessed by a professional contractor” will cost.',
        phone: '704.626.1776',
        phoneTel: 'tel:7046261776',
        communities: [
            'Asheville',
            'West Asheville',
            'Hendersonville',
            'Weaverville',
            'Black Mountain',
            'Arden',
            'Fletcher',
            'Candler',
            '+ Surrounding areas'
        ],
        quotesSectionLabel: '02 / QUOTES FOR ASHEVILLE, NC',
        faqs: [
            {
                question: 'Can you accurately quote older Asheville homes without a site visit?',
                answer: 'Yes. Our team has over 30 years in residential construction and has reviewed 2,000+ inspection reports, including plenty of older mountain housing stock. If you’d like eyes on site, we’re happy to schedule a walk-through with one of our general contractors.',
                isOpen: false
            },
            {
                question: 'Do you handle crawlspace and moisture items common in the mountains?',
                answer: 'Constantly. Crawlspace work is one of the most common categories we quote and repair, alongside plumbing, electrical, roofing, and interior/exterior items, all completed by speKwo’s full-time tradesmen.',
                isOpen: false
            },
            {
                question: 'How much does a repair quote cost?',
                answer: 'Quotes start at $59. The Itemized Solution Estimate and Pre-List Quote are $59, and The Full speKwo (every item on the report) is $89. Complete over $1,500 of repairs with speKwo and your quote cost is refunded.',
                isOpen: false
            }
        ],
        otherAreas: [
            { name: 'Charlotte', route: '/service-area-charlotte' },
            { name: 'Raleigh', route: '/service-area-raleigh' },
            { name: 'the Triad', route: '/service-area-triad' },
            { name: 'Upstate SC', route: '/service-area-upstate-sc' }
        ]
    },
    triad: {
        id: 'triad',
        name: 'The Triad, NC',
        pageTitle: 'Home Inspection Repair Quotes in the Triad, NC | 24–72 Hours | speKwo',
        heroTitlePrefix: 'Home inspection repair quotes in ',
        heroHighlight: 'the Triad',
        heroDescription: 'Across Greensboro, Winston-Salem, and High Point, the story is the same: the inspection lands, the repair list is long, and nobody agrees on what it should cost. speKwo returns one neutral repair quote in 24–72 hours, 7 days a week, a number that favors neither buyer nor seller, so Triad negotiations move instead of stalling.',
        phone: '704.626.1776',
        phoneTel: 'tel:7046261776',
        communities: [
            'Greensboro',
            'Winston-Salem',
            'High Point',
            'Kernersville',
            'Burlington',
            'Clemmons',
            'Oak Ridge',
            'Summerfield',
            '+ Surrounding areas'
        ],
        quotesSectionLabel: '02 / QUOTES FOR THE TRIAD, NC',
        faqs: [
            {
                question: 'Which Triad cities do you cover?',
                answer: 'Greensboro, Winston-Salem, High Point, and the surrounding communities — Kernersville, Burlington, Clemmons, Oak Ridge, Summerfield, and more.',
                isOpen: false
            },
            {
                question: 'Can my clients pick just the items that matter to their negotiation?',
                answer: 'Yes. That’s the Itemized Solution Estimate ($59): a quote for up to 40 items your client chooses from the report. Ideal once a DDRA is taking shape. The Full speKwo ($89) covers every repair/replace item instead.',
                isOpen: false
            },
            {
                question: 'How much does a repair quote cost?',
                answer: 'Quotes start at $59. The Itemized Solution Estimate and Pre-List Quote are $59, and The Full speKwo (every item on the report) is $89. Complete over $1,500 of repairs with speKwo and your quote cost is refunded.',
                isOpen: false
            }
        ],
        otherAreas: [
            { name: 'Charlotte', route: '/service-area-charlotte' },
            { name: 'Raleigh', route: '/service-area-raleigh' },
            { name: 'Asheville', route: '/service-area-asheville' },
            { name: 'Upstate SC', route: '/service-area-upstate-sc' }
        ]
    },
    'upstate-sc': {
        id: 'upstate-sc',
        name: 'Upstate SC',
        pageTitle: 'Home Inspection Repair Quotes in Upstate SC | 24–72 Hours | speKwo',
        heroTitlePrefix: 'Home inspection repair quotes<br class="d-br">in ',
        heroHighlight: 'Upstate South Carolina',
        heroDescription: 'From Greenville’s booming neighborhoods to the border towns of Rock Hill, Fort Mill, and Tega Cay, Upstate South Carolina deals face the same due diligence crunch as their North Carolina neighbors. speKwo returns one neutral repair quote on your inspection report in 24–72 hours, 7 days a week, with pricing tuned to South Carolina labor and material costs.',
        phone: '704.626.1776',
        phoneTel: 'tel:7046261776',
        communities: [
            'Greenville',
            'Spartanburg',
            'Simpsonville',
            'Greer',
            'Rock Hill',
            'Fort Mill',
            'Tega Cay',
            'Indian Land',
            '+ Surrounding areas'
        ],
        quotesSectionLabel: '02 / QUOTES FOR UPSTATE SC',
        faqs: [
            {
                question: 'Does speKwo price South Carolina differently than North Carolina?',
                answer: 'Costs of labor and materials vary slightly from state to state, and our quotes take that variance into account. The number you get reflects what the work actually costs in the Upstate.',
                isOpen: false
            },
            {
                question: 'Do you cover the Charlotte border towns like Fort Mill and Tega Cay?',
                answer: 'Yes. Rock Hill, Fort Mill, Tega Cay, and Indian Land are all inside our service area, along with Greenville, Spartanburg, Simpsonville, and Greer.',
                isOpen: false
            },
            {
                question: 'How much does a repair quote cost?',
                answer: 'Quotes start at $59. The Itemized Solution Estimate and Pre-List Quote are $59, and The Full speKwo (every item on the report) is $89. Complete over $1,500 of repairs with speKwo and your quote cost is refunded.',
                isOpen: false
            }
        ],
        otherAreas: [
            { name: 'Charlotte', route: '/service-area-charlotte' },
            { name: 'Raleigh', route: '/service-area-raleigh' },
            { name: 'Asheville', route: '/service-area-asheville' },
            { name: 'the Triad', route: '/service-area-triad' }
        ]
    }
};
